import React, { useEffect, useState } from "react";
import SingleFaq from "./SingleFaq";
import {
  useCreateFaqMutation,
  useUpdateFaqByIdMutation,
  useDeleteFaqByIdMutation,
  useGetFaqsByAdminIdQuery,
} from "../../../../redux/features/faq/faqApi";
import Loading from "../../../loading/LoadingPage";
import { TitleContainer, Wrapper } from "../../style";
import ColumnContainer from "../../../common/ColumnContainer";
import { TextField, Typography, styled } from "@mui/material";
import RowContainer from "../../../common/RowContainer";
import "./FaqPage.less";
import ModalComponent from "../../../common/ModalComponent";
import { DashboardButton } from "../../../common/DashboardButton";
import { useDispatch } from "react-redux";
import { getFaqs } from "../../../../redux/features/faq/faqSlice";
import { useAuth } from "../../../../utils/hooks/useAuth";
import { FormattedMessage } from "react-intl";
import { SkeletonLoading } from "../../../common/SkeletonLoading";
import { ErrorLoading } from "../../../common/ErrorLoading";

const CustomTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "orange",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "orange",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "grey",
    },
    "&:hover fieldset": {
      borderColor: "orange",
    },
    "&.Mui-focused fieldset": {
      borderColor: "orange",
    },
  },
});

export default function FaqPage() {
  //INIT
  const dispatch = useDispatch();
  const auth = useAuth();
  const adminId =
    auth?.auth?.userData?.role === "admin" && auth?.auth?.userData?.id;

  // STATE
  const [newFaq, setNewFaq] = useState({ question: "", answer: "" });
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqToDelete, setFaqToDelete] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);

  //REDUX

  const {
    data: faqs,
    isLoading,
    isError,
  } = useGetFaqsByAdminIdQuery({
    adminId: adminId,
  });
  const [createFaq] = useCreateFaqMutation();
  const [updateFaq] = useUpdateFaqByIdMutation();
  const [deleteFaq] = useDeleteFaqByIdMutation();

  // EFFECTS
  useEffect(() => {
    if (faqs) {
      dispatch(getFaqs(faqs));
    }
  }, [faqs, dispatch]);

  // FUNCTIONS
  const onEditFaq = (faq) => {
    setEditingFaq(faq);
  };

  const onDeleteFaq = (id) => {
    setFaqToDelete(id);
  };

  const handleCreateFaq = async () => {
    try {
      await createFaq({ adminId: adminId, faq: newFaq }).unwrap();
      dispatch(getFaqs(faqs));
      setNewFaq({ question: "", answer: "" });
      setOpenCreateModal(false);
    } catch (err) {
      console.error("Failed to create FAQ:", err);
    }
  };

  const handleUpdateFaq = async () => {
    try {
      const id = editingFaq.id;
      await updateFaq({
        adminId: adminId,
        faqId: id,
        faq: editingFaq,
      }).unwrap();
      dispatch(getFaqs(faqs));
      setEditingFaq(null);
    } catch (err) {
      console.error("Failed to update FAQ:", err);
    }
  };

  const handleDeleteFaq = async () => {
    try {
      const id = faqToDelete.id;
      await deleteFaq({ adminId: adminId, faqId: id }).unwrap();
      dispatch(getFaqs(faqs));
      setFaqToDelete(null);
    } catch (err) {
      console.error("Failed to delete FAQ:", err);
    }
  };

  return (
    <Wrapper>
      <ColumnContainer start style={{ height: "100vh" }} className="faq-page">
        <ColumnContainer>
          <TitleContainer>
            <Typography variant="h3" component="h3">
              <FormattedMessage id="faq.title" />
            </Typography>
          </TitleContainer>
        </ColumnContainer>
        <RowContainer end>
          <DashboardButton
            type="filled"
            onClick={() => setOpenCreateModal(true)}
          >
            <FormattedMessage id="faq.new" />
          </DashboardButton>
        </RowContainer>
        <ColumnContainer halfGap>
          {isLoading ? (
            <SkeletonLoading />
          ) : isError ? (
            <ErrorLoading />
          ) : faqs && faqs?.length > 0 ? (
            faqs?.map((faq, index) => (
              <SingleFaq
                index={index}
                key={index}
                item={faq}
                onEditFaq={onEditFaq}
                onDeleteFaq={onDeleteFaq}
              />
            ))
          ) : faqs?.faqList?.length === 0 ? (
            <Typography variant="h6" component="h6">
              <FormattedMessage id="faq.noFaqs" />
            </Typography>
          ) : null}
        </ColumnContainer>
      </ColumnContainer>

      <ModalComponent
        open={Boolean(editingFaq)}
        handleClose={() => setEditingFaq(null)}
        className="faq-modal"
      >
        <ColumnContainer style={{ gap: "3rem" }}>
          <Typography variant="h6" component="h6" color="var(--main-color)">
            <FormattedMessage id="faq.edit" />
          </Typography>
          <ColumnContainer>
            <CustomTextField
              label="Domanda"
              value={editingFaq?.question || ""}
              onChange={(e) =>
                setEditingFaq({ ...editingFaq, question: e.target.value })
              }
              fullWidth
            />
            <CustomTextField
              label="Risposta"
              value={editingFaq?.answer || ""}
              onChange={(e) =>
                setEditingFaq({ ...editingFaq, answer: e.target.value })
              }
              fullWidth
              multiline
              rows={4}
              margin="normal"
            />
          </ColumnContainer>
          <RowContainer end>
            <DashboardButton type="filled" onClick={handleUpdateFaq}>
              <FormattedMessage id="button.update" />
            </DashboardButton>
          </RowContainer>
        </ColumnContainer>
      </ModalComponent>

      <ModalComponent
        open={Boolean(faqToDelete)}
        handleClose={() => setFaqToDelete(null)}
        className="faq-modal"
      >
        <ColumnContainer align="center">
          <Typography variant="h6" component="h6">
            <FormattedMessage id="faq.delete" /> #{faqToDelete?.id}?
          </Typography>
          <RowContainer end minGap>
            <DashboardButton type="filled" onClick={handleDeleteFaq}>
              <FormattedMessage id="button.delete" />
            </DashboardButton>

            <DashboardButton
              type="outlined"
              onClick={() => setFaqToDelete(null)}
            >
              <FormattedMessage id="button.cancel" />
            </DashboardButton>
          </RowContainer>
        </ColumnContainer>
      </ModalComponent>
      <ModalComponent
        open={openCreateModal}
        handleClose={() => setOpenCreateModal(false)}
        className="faq-modal"
      >
        <ColumnContainer>
          <Typography variant="h6" component="h6" color="var(--main-color)">
            <FormattedMessage id="faq.create" />
          </Typography>
          <CustomTextField
            label={<FormattedMessage id="faq.question" />}
            value={newFaq.question}
            onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
            fullWidth
          />
          <CustomTextField
            label={<FormattedMessage id="faq.answer" />}
            value={newFaq.answer}
            onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
            fullWidth
            multiline
            rows={4}
            margin="normal"
          />
          <RowContainer end>
            <DashboardButton type="filled" onClick={handleCreateFaq}>
              <FormattedMessage id="button.confirm" />
            </DashboardButton>
          </RowContainer>
        </ColumnContainer>
      </ModalComponent>
    </Wrapper>
  );
}
